import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

export interface ResourceProvider {
  id: string
  name: string
}

const sortProviders = (providers: ResourceProvider[]): ResourceProvider[] => {
  return [...providers].sort((left, right) => {
    const byName = left.name.localeCompare(right.name, undefined, {
      numeric: true,
      sensitivity: "base",
    })
    if (byName !== 0) {
      return byName
    }

    return left.id.localeCompare(right.id, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  })
}

const getResourceProviders = async (
  organizationId: Organization["id"]
): Promise<ResourceProvider[]> => {
  try {
    const response = await httpClient.get<ResourceProvider[]>(
      `/api/v1/organizations/${organizationId}/pipelines/resource-providers`
    )

    return sortProviders(response.data)
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseResourceProvidersOptions {
  organizationId: Organization["id"]
}

export const useResourceProviders = ({
  organizationId,
}: UseResourceProvidersOptions) => {
  const queryKey = useQueryKey(ApiQueryName.Credentials, "getMany", null, {
    organizationId,
    resource: "providers",
  })

  const queryResult = useQuery<ResourceProvider[], HttpError>({
    enabled: Boolean(organizationId),
    placeholderData: [],
    queryFn: async () => getResourceProviders(organizationId),
    queryKey,
  })

  return useRenameQueryResult(queryResult, "resourceProviders")
}
